<template>
    <Modal v-if="visible" @cancel="onCancel">
        <ModalCard
            :active-validate="false"
            :content-max-height="contentMaxHeight"
            :headerTitle="title"
            :height="height"
            :max-width="maxWidth"
            :width="width"
            :withoutBotNav="true"
            @cancel="onCancel"
        >
            <div class="container">
                <div class="filter-container filters">
                    <Filter
                        :equipementList="equipementList"
                        :filter="filter"
                        :searchSite="searchSite"
                        :siteList="siteList"
                        :siteListTotal="siteListTotal"
                        class="filter"
                        @addFilterEquipement="addFilterEquipement"
                        @addFilterSite="addFilterSite"
                        @clearFilter="clearFilter"
                        @equipementToggleSelectAll="equipementToggleSelectAll"
                        @multipleSelectSearchSite="multipleSelectSearchSite"
                        @removeFilterEquipement="removeFilterEquipement"
                        @removeFilterSite="removeFilterSite"
                        @search="search"
                        @siteToggleSelectAll="siteToggleSelectAll"
                    />
                </div>
                <div class="dataTable">
                    <div class="titlePreavis">
                        <h2>
                            {{ count }} {{ $tc('preavisIntervention.preavis', count) }}
                            <ReglePastille cle="preavisIntervention.definition" />
                            <ReglePastille cle="preavisIntervention.portee" />
                        </h2>
                    </div>
                    <LoaderComponent v-if="isLoading" class="loader" />

                    <div v-else class="datatable-container">
                        <DataTable
                            :count="count"
                            :isLoading="isLoading"
                            :not-sticky="true"
                            :stop-propa-on-pdf="true"
                            :tableData="tableData"
                            :tableHeader="tableHeader"
                            @delete="onDelete($event)"
                            @edit="onEdit($event)"
                            @sort="doSort($event)"
                            @toggleActif="onToggleActif($event)"
                        />
                    </div>
                </div>

                <div class="actions-bar">
                    <ButtonBlue
                        :on-click="openCreate"
                        :text="$t('modale.preavisIntervention.button.create')"
                        class="button-create btn"
                    />
                </div>
            </div>
        </ModalCard>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Modal from '@/components/ui-components/Modal.vue';
import ModalCard from '@/components/ui-components/ModalCard.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import Filter from '@/modules/Common/components/Filter.vue';
import { store } from '@/plugins/store';
import { PreavisInterventionModuleAction } from '@/modules/PreavisIntervention/store/actions-types';
import { PREAVIS } from '@/modules/PreavisIntervention/store/mutation-types';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import EquipementService from '@/modules/Equipements/services/EquipementService';
import { Site } from '@/models/Site';
import { EquipementFilter } from '@/models/EquipementFilter';
import { PreavisInterventionDTO } from '@/models/PreavisIntervention/PreavisInterventionDTO';
import { SortType, sortValue } from '@/models/SortType';
import { TableHeader } from '@/models/TableHeader';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'PreavisConsultModal',
    components: { Modal, ModalCard, ButtonBlue, DataTable, LoaderComponent, Filter, ReglePastille },
    props: {
        visible: { type: Boolean, required: true },
        title: { type: String, default: () => "Préavis d'intervention" },
        width: { type: String, default: () => '75vw' },
        maxWidth: { type: String, default: () => '80%' },
        contentMaxHeight: { type: String, default: () => '75vh' },
        height: { type: String, default: () => '75vh' },
    },
    emits: ['cancel', 'open-create', 'edit', 'delete', 'toggleActif'],
    beforeMount() {
        if (this.$store.state.preavisInterventionState.items.length === 0) {
            this.search();
        }

        this.loadSites('');
    },
    data() {
        return {
            PreavisInterventionModuleAction,
            siteList: [] as Site[],
            siteListTotal: [] as Site[],
            searchSite: '' as string,
            equipementList: [] as EquipementFilter[],
            filter: {
                siteList: [] as Site[],
                equipementList: [] as EquipementFilter[],
            },
        };
    },
    computed: {
        items(): PreavisInterventionDTO[] {
            return store.state.preavisInterventionState.items;
        },
        tableHeader() {
            return store.getters['preavisInterventionState/tableHeader'];
        },
        tableData() {
            return store.getters['preavisInterventionState/tableData'];
        },
        count(): number {
            return store.getters['preavisInterventionState/count'];
        },
        isLoading(): boolean {
            return store.state.preavisInterventionState.isLoadingList;
        },
    },
    watch: {
        visible(newVal: boolean) {
            if (!newVal) {
                this.clearFilter();
                this.search();
            }
        },
    },
    methods: {
        onCancel() {
            this.$emit('cancel');
        },
        openCreate() {
            this.$emit('open-create');
        },
        resolveDto(row: any) {
            return this.items.find(i => i.id === row?.idMasque) || null;
        },
        onEdit(row: any) {
            const dto = this.resolveDto(row);
            if (dto) this.$emit('edit', dto);
        },
        onDelete(row: any) {
            const dto = this.resolveDto(row);
            if (dto) this.$emit('delete', dto);
        },
        onToggleActif(row: any) {
            const dto = this.resolveDto(row);
            if (dto) this.$emit('toggleActif', dto);
        },
        refreshList() {
            this.search();
        },
        loadSites(search: string) {
            SiteListService.getSiteList(new SiteSearchDTO(0, search, true)).then(res => {
                this.siteList = res.data.map(({ id, chantierId, description }: any) => ({
                    id,
                    chantierId,
                    description,
                }));
                this.siteListTotal = this.siteList.slice();
            });
        },
        async loadEquipementsForSelectedSites() {
            const ids = (this.filter.siteList || []).map((s: Site) => s.id);
            if (!ids.length) {
                this.equipementList = [];
                this.filter.equipementList = [];
                return Promise.resolve();
            }
            const res = await EquipementService.getEquipementBySites(ids as any, '', '');
            const items = res.data?.content || res.data || [];
            this.equipementList = items.map((it: any) => ({
                id: it?.id ?? it?.phaseId ?? it?.equipementId,
                libelle: it?.libelle ?? it?.designation ?? it?.nom ?? '',
                phaseLibelle: it?.phaseLibelle ?? it?.phase?.libelle ?? '',
                groupeId: String(it?.groupeId ?? ''),
                sousGroupeId: it?.sousGroupeId ?? 0,
            }));
        },
        addFilterSite(site: Site) {
            if (!this.filter.siteList.find((s: Site) => s.id === site.id)) this.filter.siteList.push(site);
            this.loadEquipementsForSelectedSites();
        },
        removeFilterSite(site: Site) {
            this.filter.siteList = this.filter.siteList.filter((s: Site) => s.id !== site.id);
            this.loadEquipementsForSelectedSites();
        },
        siteToggleSelectAll() {
            const allSelected =
                this.filter.siteList.length === this.siteListTotal.length && this.siteListTotal.length > 0;
            this.filter.siteList = allSelected ? [] : this.siteListTotal.slice();
            this.loadEquipementsForSelectedSites();
        },
        multipleSelectSearchSite(search: string) {
            this.searchSite = search;
            this.loadSites(search);
        },
        addFilterEquipement(equipement: EquipementFilter) {
            if (!this.filter.equipementList.find((e: EquipementFilter) => e.id === equipement.id))
                this.filter.equipementList.push(equipement);
        },
        removeFilterEquipement(equipement: EquipementFilter) {
            this.filter.equipementList = this.filter.equipementList.filter(
                (e: EquipementFilter) => e.id !== equipement.id
            );
        },
        equipementToggleSelectAll() {
            const allSelected =
                this.filter.equipementList.length === this.equipementList.length && this.equipementList.length > 0;
            this.filter.equipementList = allSelected ? [] : this.equipementList.slice();
        },
        clearFilter() {
            this.filter.siteList = [];
            this.filter.equipementList = [];
            this.equipementList = [];
        },

        search() {
            const siteIds = (this.filter.siteList || []).map((s: Site) => s.id);
            const equipementIds = (this.filter.equipementList || []).map((e: EquipementFilter) => e.id);
            const sortHeader = (this.tableHeader as TableHeader[]).find(
                h => h.sortStatus !== SortType.NONE && h.sortStatus !== SortType.INACTIVE
            );
            const sort = sortHeader ? sortValue(sortHeader.sortData, sortHeader.sortStatus) : 'dateCreation,desc';
            store.dispatch(`preavisInterventionState/${PreavisInterventionModuleAction.GET_LIST}`, {
                siteIds,
                equipementIds,
                sort,
            });
        },
        doSort(sortEvent: { sortData: string; sortStatus: SortType }) {
            store.commit(`preavisInterventionState/${PREAVIS.SET_SORT}`, sortEvent);
            this.search();
        },
    },
});
</script>

<style scoped>
.container {
    display: flex;
    flex-direction: column;
}
.dataTable {
    flex: 1;
    margin-top: 8px;
}

.actions-bar {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}
.button-create {
    margin-left: 16px;
}
.btn {
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 10px;
}
.datatable-container {
    height: 50vh;
}
.titlePreavis {
    margin-bottom: 10px;
}
</style>
